import { getEnv } from "@/config/env";
import { getJson, postJson } from "@/api/http";
import type { MemberProductsViewModel } from "@/types/memberProducts";
import type { Result } from "@/types/layout";

const MEMBER_PRODUCTS_ENDPOINT = "/member/products";
const CART_DEVICE_SUBSCRIPTION_ENDPOINT = "/cart/device-subscription";

export async function fetchMemberProducts(
  signal?: AbortSignal
): Promise<Result<MemberProductsViewModel>> {
  const env = getEnv();
  const apiUrl = `${env.apiBaseUrl}${MEMBER_PRODUCTS_ENDPOINT}`;
  return getJson<MemberProductsViewModel>(
    apiUrl,
    signal ? { signal } : undefined
  );
}

export async function cancelDeviceSubscription(
  deviceId: string
): Promise<Result<null>> {
  const env = getEnv();
  const apiUrl = `${env.apiBaseUrl}${MEMBER_PRODUCTS_ENDPOINT}/${encodeURIComponent(deviceId)}/cancel-subscription`;
  const result = await postJson<unknown>(apiUrl, {});
  return result.ok ? { ok: true, data: null } : result;
}

/**
 * Takes back a pending cancellation of the device's SIM subscription so it keeps
 * renewing on its existing billing date — the device counterpart of
 * `resumeMembership`, through the same backend resume path. Charges nothing and
 * creates no new subscription.
 *
 * Idempotent server-side: resuming a subscription that already renews is a no-op.
 * A 404 means the cancelled period has ended; the device then gets a new
 * subscription through the normal subscribe flow.
 */
export async function resumeDeviceSubscription(
  deviceId: string
): Promise<Result<null>> {
  const env = getEnv();
  const apiUrl = `${env.apiBaseUrl}${MEMBER_PRODUCTS_ENDPOINT}/${encodeURIComponent(deviceId)}/resume-subscription`;
  const result = await postJson<unknown>(apiUrl, {});
  return result.ok ? { ok: true, data: null } : result;
}

/**
 * Adds a device subscription to the in-shop digital cart and returns success.
 * The caller is expected to navigate to /checkout once this resolves. This
 * replaces the legacy form-POST → Stripe-hosted checkout used by the Remix
 * shop; the v1 `/cart/order-subscription` endpoint is intentionally untouched.
 *
 * The `deviceId` is the composite identifier the backend uses to look the
 * device up (IMEI when present, else shareName, else liveId). The `imei` is
 * the raw IMEI that gets stored as Stripe metadata so it can be referenced
 * from subscription webhooks. Pass null for `imei` only if the caller knows
 * the device has none — the picker filters those out, so in practice this
 * should always be a non-null string for device-subscription purchases.
 *
 * Uses a 60s timeout (vs the default 10s) — the BE has to delete pre-existing
 * carts, resolve the License + subscription, and resolve a Stripe price by
 * culture, which together can blow past the default on slow links.
 */
export async function addDeviceSubscriptionToCart(args: {
  productSku: string;
  deviceId: string;
  imei: string | null;
  subscriptionId?: string;
}): Promise<Result<null>> {
  const env = getEnv();
  const apiUrl = `${env.apiBaseUrl}${CART_DEVICE_SUBSCRIPTION_ENDPOINT}`;
  const result = await postJson<unknown>(apiUrl, args, { timeout: 60000 });
  return result.ok ? { ok: true, data: null } : result;
}
